import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Modal, Segmented, Table, Tag, Tooltip, message } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { listMaterializationLogs } from '../modules/order-schedules/api';

/**
 * History of the nightly recurring materialisation.
 *
 * The log has existed since OVO-113 (`schedule_materialization_logs`, exposed
 * by `GET /partners/:id/schedules/logs`) but nothing rendered it — so when a
 * recurring order failed to appear, or appeared when it shouldn't have, there
 * was no way to see what the job did. Every attempt is recorded: OK with the
 * created order, SKIPPED with the reason (usually "already created for that
 * day" — re-runs are safe by design), or FAILED with the error.
 */
const STATUS_META = {
  OK: { color: 'green', label: 'Created' },
  SKIPPED: { color: 'default', label: 'Skipped' },
  FAILED: { color: 'red', label: 'Failed' },
};

export default function MaterializationHistoryModal({ open, partnerId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('ALL');

  const load = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const { response } = await listMaterializationLogs(partnerId, {
        limit: 200,
        ...(filter === 'ALL' ? {} : { status: filter }),
      });
      setRows(Array.isArray(response) ? response : response?.items || []);
    } catch (e) {
      message.error(e?.message || 'Failed to load the history');
    } finally {
      setLoading(false);
    }
  }, [partnerId, filter]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const columns = [
    {
      title: 'Ran at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'For delivery date',
      dataIndex: 'attemptedDate',
      key: 'attemptedDate',
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, r) => (
        <span>
          #{r.scheduleId}
          {r.schedule?.deliverySite?.name ? ` · ${r.schedule.deliverySite.name}` : ''}
        </span>
      ),
    },
    {
      title: 'Result',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const meta = STATUS_META[s] || { color: 'default', label: s };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Order',
      dataIndex: 'createdOrderId',
      key: 'createdOrderId',
      render: (id, r) =>
        r.createdOrder?.orderCode ? (
          <Link to={`/admin/orders/${r.createdOrder.orderCode}`}>{r.createdOrder.orderCode}</Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Detail',
      dataIndex: 'reason',
      key: 'reason',
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ color: 'var(--text-muted)' }}>{String(v).slice(0, 70)}</span>
          </Tooltip>
        ) : (
          ''
        ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={980} title="Recurring — materialization history">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Every night at 06:00 UTC the job creates this partner's recurring orders for the days inside the horizon."
        description={'"Skipped" is normal — a day already created is never duplicated, so re-runs are safe.'}
      />
      <Segmented
        style={{ marginBottom: 12 }}
        value={filter}
        onChange={setFilter}
        options={[
          { label: 'All', value: 'ALL' },
          { label: 'Created', value: 'OK' },
          { label: 'Skipped', value: 'SKIPPED' },
          { label: 'Failed', value: 'FAILED' },
        ]}
      />
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 15, showSizeChanger: false }}
        scroll={{ x: 'max-content', y: 420 }}
        locale={{ emptyText: 'No materialization recorded yet for this partner.' }}
      />
    </Modal>
  );
}
